import React, { useEffect, useMemo, useRef, useState } from 'react';

function normalize(value) {
  return String(value == null ? '' : value).trim();
}

function SearchableSelect({
  id,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = 'Select option…',
  ariaInvalid = false,
  ariaLabel = ''
}) {
  const rootRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');

  const normalizedOptions = useMemo(
    () => (Array.isArray(options) ? options : []),
    [options]
  );
  const selected = useMemo(
    () => normalizedOptions.find((opt) => normalize(opt.value).toLowerCase() === normalize(value).toLowerCase()) || null,
    [normalizedOptions, value]
  );

  useEffect(() => {
    setQuery(selected ? normalize(selected.label) : '');
  }, [selected]);

  useEffect(() => {
    function handleDocumentMouseDown(event) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, []);

  const filtered = useMemo(() => {
    const term = normalize(query).toLowerCase();
    if (!term) return normalizedOptions;
    return normalizedOptions.filter((opt) => normalize(opt.label).toLowerCase().includes(term));
  }, [normalizedOptions, query]);

  return (
    <div className="widget-search-select" ref={rootRef}>
      <div className="widget-search-select-input-wrap">
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={menuOpen ? 'true' : 'false'}
          aria-controls={`${id}-listbox`}
          aria-autocomplete="list"
          aria-invalid={ariaInvalid ? 'true' : 'false'}
          aria-label={ariaLabel || placeholder}
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          onFocus={() => {
            if (!disabled) setMenuOpen(true);
          }}
          onChange={(event) => {
            const next = event.target.value;
            setQuery(next);
            setMenuOpen(true);
            if (!next.trim()) onChange('');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setMenuOpen(false);
            }
            if (event.key === 'ArrowDown') {
              setMenuOpen(true);
            }
            if (event.key === 'Enter' && filtered.length > 0) {
              event.preventDefault();
              onChange(normalize(filtered[0].value));
              setMenuOpen(false);
            }
          }}
          className={`widget-form-input widget-search-select-input${ariaInvalid ? ' has-error' : ''}`}
        />
        <span className="widget-search-select-caret" aria-hidden="true">v</span>
      </div>
      {menuOpen && filtered.length > 0 && !disabled && (
        <div className="widget-search-select-menu" id={`${id}-listbox`} role="listbox" aria-label={`${ariaLabel || placeholder} options`}>
          {filtered.map((option) => (
            <button
              key={`${id}-${option.value}`}
              type="button"
              className="widget-search-select-option"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(normalize(option.value));
                setMenuOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
